<template>
  <RCard class="terms">
    <div class="text-subtitle-1 text-secondary-2">Terms</div>

    <Grid gtc="repeat(3, 1fr)" gap="var(--r-space-md)" align-content="start" align-items="start" class="q-mt-md">
      <Grid
        v-for="term of slicesTerms"
        :key="term.id"
        gtr="auto 1fr"
        class="term border-radius-sm overflow-hidden"
        :class="term.termStatus.name"
      >
        <Grid
          :gtc="term.qualifiers.length ? '1fr auto' : '1fr'"
          align-items="center"
          class="q-pa-sm q-pr-lg"
          :class="term.headerBgClass"
        >
          <Grid>
            <div class="text-lowercase-btn" :class="term.labelColorClass">{{ term.termStatus.label }}</div>
            <div :class="term.nameClasses" class="text-ellipsis">
              {{ term.name }}
            </div>
          </Grid>

          <div
            v-if="term.qualifiers.length"
            class="chip text-ellipsis text-lowercase-btn text-secondary-1 q-px-sm bg-white-1"
          >
            {{ term.qualifiers.map(q => q.name).join(',') }}
            <LmTooltip>{{ term.qualifiers.map(q => q.name).join(', ') }}</LmTooltip>
          </div>
        </Grid>

        <div :class="{ 'bg-white-2': term.termStatus.name === 'allowed' }" class="description q-pa-md">
          <Description :description="term.description" />
        </div>
      </Grid>
    </Grid>

    <Grid v-if="slicesTerms.length" justify-content="center" class="q-pt-md">
      <ShowMoreLess v-model="showMore" />
    </Grid>

    <span v-else class="text-subtitle-1 text-secondary-1">
      This license period is not associated with any terms. Edit this period to add terms.
    </span>
  </RCard>
</template>

<script>
// Components
import ShowMoreLess from '@/reusable/ShowMoreLess'
import Description from './Description'

const NOT_APPLICABLE = 'not-applicable'
const ALLOWED = 'allowed'
const NOT_ALLOWED = 'not-allowed'
const UNKNOWN = 'unknown'

export default {
  components: {
    ShowMoreLess,
    Description
  },

  props: {
    data: {
      type: Array,
      default: () => []
    }
  },

  data: () => ({
    showMore: false
  }),

  computed: {
    terms() {
      return this.data.reduce((acc, curr) => {
        const termStatus = this.getTermStatus(curr)

        acc.push({
          ...curr,
          termStatus: termStatus,
          labelColorClass: this.getLabelColorClass(termStatus.name),
          nameClasses: this.getNameClasses(termStatus.name),
          headerBgClass: this.getHeaderBgClass(termStatus.name)
        })

        return acc
      }, [])
    },

    slicesTerms() {
      if (!this.showMore) return this.terms.slice(0, 6)
      return this.terms
    }
  },

  methods: {
    getTermStatus(term) {
      if (!term.active) return { name: NOT_APPLICABLE, label: 'not applicable' }
      if (term.status.name === 'yes') return { name: ALLOWED, label: 'allowed' }
      if (term.status.name === 'no') return { name: NOT_ALLOWED, label: 'not allowed' }
      if (term.status.name === 'n/a') return { name: UNKNOWN, label: 'unknown' }
    },

    getLabelColorClass(termStatusName) {
      if (termStatusName === NOT_APPLICABLE) return 'text-grey3'
      if (termStatusName === UNKNOWN) return 'text-secondary-1'
      return 'text-white-1'
    },

    getNameClasses(termStatusName) {
      if (termStatusName === NOT_APPLICABLE) return 'text-subtitle-1 text-grey2'
      if (termStatusName === UNKNOWN) return 'text-subtitle-1 text-secondary-1'
      return 'text-heading-3 text-white-1'
    },

    getHeaderBgClass(termStatusName) {
      if (termStatusName === NOT_APPLICABLE) return 'bg-white-1'
      if (termStatusName === UNKNOWN) return 'bg-white-3'
      if (termStatusName === NOT_ALLOWED) return 'bg-graph-13'
      return 'bg-graph-to-renew'
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/shadows';

.terms {
  .term {
    min-height: 135px;

    &.not-applicable {
      border: 1px solid var(--r-white-4);

      .description {
        color: var(--r-grey-2);
      }
    }

    &.allowed {
      @extend .shadow-low;

      .description {
        color: var(--r-secondary-2);
      }
    }

    &.unknown .description {
      border: 1px solid var(--r-white-4);
      color: var(--r-secondary-1);
    }

    &.not-allowed .description {
      border: 1px solid var(--r-white-4);
      color: var(--r-secondary-2);
    }

    .chip {
      max-width: 100px;
      border-radius: var(--r-space-md);
    }
  }
}
</style>
